/**
 * MakeOfferCard — submit an offer from a partner link.
 *
 * The marketplace's "Submit offer" opens a page of its own; behind a link there
 * is nowhere to navigate, so the same button reveals the investor's own offer
 * fields in place. The page decides whether the card shows at all.
 */

import { useState } from 'react'
import { useForm, type FieldPath } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/common/components/button'
import { Text } from '@/common/components/text'
import {
  OFFER_FORM_DEFAULT_VALUES,
  OfferFormFields,
  offerFormSchema,
  type OfferFormData,
} from '@/features/offers/components/OfferForm'
import { createPartnerOffer, type PartnerOfferCreate } from '../../api/partnerLinks'
import {
  mutationFailureOf,
  readableIssue,
  usePartnerMutation,
  validationIssuesOf,
  type PartnerLinkSession,
} from '../../hooks/usePartnerMutation'
import * as page from '../PartnerPropertyPage/PartnerPropertyPage.css'
import * as styles from '../partnerCards.css'

export interface MakeOfferCardProps {
  session: PartnerLinkSession
  /** The property the session is bound to. */
  propertyId: string
  /** Start with the form revealed. */
  initialOpen?: boolean
}

const OFFER_FIELDS: ReadonlyArray<FieldPath<OfferFormData>> = [
  'offer_price',
  'earnest_money_deposit',
  'closing_timeline',
  'notes',
  'private_notes',
]

const HEADING = 'Make an offer'

/** The same mapping the signed-in submit-offer page sends. */
function toRequestBody(data: OfferFormData, offerPrice: number): PartnerOfferCreate {
  return {
    offer_price: offerPrice,
    earnest_money_deposit: data.earnest_money_deposit ?? undefined,
    closing_timeline: data.closing_timeline,
    notes: data.notes || undefined,
    private_notes: data.private_notes || undefined,
  }
}

function isOfferField(value: string | number | undefined): value is FieldPath<OfferFormData> {
  return OFFER_FIELDS.some((field) => field === value)
}

export function MakeOfferCard({ session, propertyId, initialOpen = false }: MakeOfferCardProps) {
  const [open, setOpen] = useState(initialOpen)
  // Shown when the offer went out but the link cannot read it back.
  const [sent, setSent] = useState(false)
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<OfferFormData>({
    resolver: zodResolver(offerFormSchema) as never,
    defaultValues: OFFER_FORM_DEFAULT_VALUES,
  })
  const submit = usePartnerMutation(session, (body: PartnerOfferCreate) =>
    createPartnerOffer(session.token, propertyId, body)
  )

  const onSubmit = (data: OfferFormData) => {
    // The schema already requires it; the inferred type does not say so.
    if (data.offer_price === undefined) {
      setError('offer_price', { message: 'Offer price is required' })
      return
    }
    submit.mutate(toRequestBody(data, data.offer_price), {
      onSuccess: () => {
        reset(OFFER_FORM_DEFAULT_VALUES)
        setOpen(false)
        setSent(true)
      },
      onError: (error) => {
        for (const issue of validationIssuesOf(error)) {
          const field = issue.loc[1]
          setError(isOfferField(field) ? field : 'root', { message: readableIssue(issue) })
        }
      },
    })
  }

  const cancel = () => {
    reset(OFFER_FORM_DEFAULT_VALUES)
    submit.reset()
    setOpen(false)
  }

  if (!open) {
    return (
      <section className={page.railCard} aria-label={HEADING}>
        <Text as="h2" variant="body" weight="bold">
          {HEADING}
        </Text>
        <Text variant="bodySm" color="muted">
          {sent
            ? 'Your offer was sent to the listing agent.'
            : 'Your offer goes to the listing agent alongside every other offer on this property.'}
        </Text>
        <Button
          type="button"
          variant="primary"
          width="full"
          onClick={() => {
            setSent(false)
            setOpen(true)
          }}
        >
          Submit offer
        </Button>
      </section>
    )
  }

  const temporaryFailure = submit.isError && mutationFailureOf(submit.error) === 'temporary'

  return (
    <section className={page.railCard} aria-label={HEADING}>
      <Text as="h2" variant="body" weight="bold">
        {HEADING}
      </Text>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <OfferFormFields control={control} errors={errors} disabled={submit.isPending} />
        {errors.root?.message && (
          <span className={styles.errorMessage} role="alert">
            {errors.root.message}
          </span>
        )}
        {temporaryFailure && (
          <span className={styles.errorMessage} role="alert">
            Your offer was not sent. Check your connection and try again.
          </span>
        )}
        <Button type="submit" variant="primary" width="full" disabled={submit.isPending}>
          {submit.isPending ? 'Submitting...' : 'Submit offer'}
        </Button>
        <Button type="button" variant="secondary" width="full" onClick={cancel} disabled={submit.isPending}>
          Cancel
        </Button>
      </form>
    </section>
  )
}
